/**
 *
 * CommunicationPreview
 *
 */

import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// styles
import './style.scss';

import moment from 'moment';

// antd component
import { Col, Row } from 'antd';

// components
import ThumbnailUpload from 'components/ThumbnailUpload';

// Communication components
import {
  FBDocumentsPreview,
  FBFormsPreview,
  FBAppointmentPreview,
  FBDiscussion,
  MediaPreview,
} from 'components/Communications';

import {
  getCommunicationType,
  getCommunicationTypeLabel,
} from 'utils/helpers/helper.generateData';

// utils
import {
  prepearFormValue,
  prepearFormValueRemoveHiddenChain,
} from 'utils/forms/forms.utils';
// hooks
import { useRefState } from 'utils/hooks';

const CommunicationPreview = React.forwardRef(
  (
    {
      mode,
      dateFormat,
      timeFormat,
      element,
      dataSource,
      mainFormFields,
      headFormFields,
      // dafault states
      hideForm,
      hideAppointment,
      hideDocument,
      hideDiscussion,
      hideThumbnail,
      // actions
      onViewDiscussionClick,
      // default
      className,
    },
    ref,
  ) => {
    const childClassNames = classNames('preview-wrapper', className);

    const [generalElement, generalElementRef, setGeneralElement] = useRefState(
      null,
    );

    const generateObjectFromForm = useCallback(
      formValues => {
        if (dataSource && dataSource?.length > 0) {
          const sendingData = prepearFormValue(dataSource, formValues);
          const sendingDataClear =
            sendingData &&
            prepearFormValueRemoveHiddenChain(dataSource, sendingData);
          return sendingDataClear || {};
        } else {
          return {};
        }
      },
      [dataSource],
    );

    useEffect(() => {
      if (element || mainFormFields || headFormFields) {
        const mainObj = generateObjectFromForm(mainFormFields);
        let elementObj = {
          ...element,
          ...mainObj,
          ...headFormFields,
        };
        setGeneralElement(elementObj);
      }
    }, [element, mainFormFields, headFormFields]);

    return (
      generalElement && (
        <div ref={ref} className={childClassNames}>
          <Row gutter={[0, 0]}>
            <Col xs={24} className="preview-section header">
              <div className="preview-header">
                {generalElement?.thumbnail && !!!hideThumbnail && (
                  <div className="preview-header__thumbnail">
                    <ThumbnailUpload
                      onlyRead={true}
                      value={generalElement?.thumbnail}
                      size={ mode == 'onlyRead' ? 'small' : 'medium'}
                    />
                  </div>
                )}

                <div className="preview-header__content">
                  <span className="meta-date">{`${
                    generalElement?.deadlineDate
                      ? moment(generalElement?.deadlineDate).format(dateFormat)
                      : generalElement?.sentDate
                      ? moment(generalElement?.sentDate).format(dateFormat)
                      : moment(generalElement?.creationDate).format(dateFormat)
                  }`}</span>
                  {!!generalElement?.reason && (
                    <h3 className="preview-header__sub-title">
                      {generalElement?.title}
                    </h3>
                  )}
                  {!generalElement?.reason ? (
                    <h3 className="preview-header__title">
                      {generalElement?.title}
                    </h3>
                  ) : (
                    <h3 className="preview-header__title">
                      {generalElement?.reason}
                    </h3>
                  )}
                  {mode == 'preview' && (
                    generalElement?.types && (
                      <span className="tag-types">
                        {getCommunicationType(generalElement)?.map(
                          (typeItem, i) =>
                            typeItem !== 'no-type' && (
                              <span
                                key={typeItem + '-preview-type-' + i}
                                className={`tag-type tag-type--${typeItem}`}
                              >
                                {getCommunicationTypeLabel(typeItem)?.label}
                              </span>
                            ),
                        )}
                      </span>
                    )
                  )}
                </div>
              </div>

              <div className="preview-header__bottom">
                {mode == 'onlyRead' && (
                  generalElement?.types && (
                    <span className="tag-types">
                      {getCommunicationType(generalElement)?.map(
                        (typeItem, i) =>
                          typeItem !== 'no-type' && (
                            <span
                              key={typeItem + '-preview-type-' + i}
                              className={`tag-type tag-type--${typeItem}`}
                            >
                              {getCommunicationTypeLabel(typeItem)?.label}
                            </span>
                          ),
                      )}
                    </span>
                  )
                )}
              </div>

            </Col>

            {generalElement?.description && (
              <Col xs={24} className="preview-section">
                <div
                  className="preview-description"
                  dangerouslySetInnerHTML={{
                    __html: generalElement?.description,
                  }}
                />
              </Col>
            )}
            {generalElement?.media?.length > 0 && (
              <Col xs={24} className="preview-section">
                <MediaPreview media={generalElement?.media} />
              </Col>
            )}
            {!!generalElement?.forms && !!!hideForm && (
              <Col xs={24}>
                <FBFormsPreview forms={generalElement?.forms} />
              </Col>
            )}
            {!!generalElement?.appointments && !!!hideAppointment && (
              <Col xs={24}>
                <FBAppointmentPreview
                  appointments={generalElement?.appointments}
                />
              </Col>
            )}
            {!!generalElement?.documents && !!!hideDocument && (
              <Col xs={24}>
                <FBDocumentsPreview documents={generalElement?.documents} />
              </Col>
            )}
            {!!!hideDiscussion &&
              ((!!generalElement?.types?.includes('discussion') ||
                !!generalElement?.discussion) && (
                <Col xs={24} className="col-discussion">
                  <FBDiscussion
                    mode="preview"
                    discussionId={generalElement?.discussion}
                    onViewDiscussionClick={
                      mode == 'onlyRead' && onViewDiscussionClick
                    }
                  />
                </Col>
              ))}
          </Row>
        </div>
      )
    );
  },
);

CommunicationPreview.defaultProps = {
  mode: 'preview',
  dateFormat: 'DD.MM.YYYY',
  timeFormat: 'HH:mm',
  dataSource: [],
  mainFormFields: {},
  headFormFields: {},
  hideForm: false,
  hideAppointment: false,
  hideDocument: false,
  hideDiscussion: false,
  hideThumbnail: false,
};
CommunicationPreview.propTypes = {
  mode: PropTypes.oneOf(['edit', 'onlyRead', 'preview', 'new']).isRequired,
  mainFormFields: PropTypes.oneOfType([
    PropTypes.object.isRequired,
    PropTypes.bool.isRequired,
  ]),
  element: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  headFormFields: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  dataSource: PropTypes.oneOfType([
    PropTypes.array.isRequired,
    PropTypes.bool.isRequired,
  ]),
};

export default CommunicationPreview;
